import { getAnalyticsProjectId } from './config'
import { getAppInfo } from './appInfo'
import { ANALYTICS_RESERVED_KEYS } from './reservedKeys'
import { sanitizeAnalyticsProps } from './sanitize'
import type { AnalyticsEventPayload, AnalyticsTrackInput } from './types'

function coerceIdentity(value: unknown): string | number | null {
  if (value == null) return null
  if (typeof value === 'string') {
    const t = value.trim()
    return t || null
  }
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const text = String(value).trim()
  return text || null
}

export function buildAnalyticsEvent(input: AnalyticsTrackInput): AnalyticsEventPayload | null {
  const eventName = typeof input.event_name === 'string' ? input.event_name.trim() : ''
  if (!eventName) return null

  const info = getAppInfo()
  const custom: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(input)) {
    if (k === 'event_name') continue
    if (ANALYTICS_RESERVED_KEYS.has(k)) continue
    custom[k] = v
  }

  const sanitizedCustom = sanitizeAnalyticsProps(custom)
  const userId = coerceIdentity(input.user_id) ?? info.user_id
  const deviceId =
    (typeof input.device_id === 'string' && input.device_id.trim() ? input.device_id.trim() : null) ||
    info.device_id ||
    null
  const accountId = coerceIdentity(input.account_id) ?? (userId != null ? String(userId) : null)

  if (userId == null && !deviceId && accountId == null) return null

  const payload: AnalyticsEventPayload = {
    project_id: getAnalyticsProjectId(),
    event_name: eventName,
    client_ts: typeof input.client_ts === 'number' ? input.client_ts : Date.now(),
    session_id: typeof input.session_id === 'string' ? input.session_id : info.session_id,
    os: typeof input.os === 'string' ? input.os : info.os,
    app_version: typeof input.app_version === 'string' ? input.app_version : info.app_version,
    device_type: typeof input.device_type === 'string' ? input.device_type : info.device_type,
    scre: typeof input.scre === 'string' ? input.scre : info.scre,
    channel: typeof input.channel === 'string' ? input.channel : info.channel,
    la: typeof input.la === 'string' ? input.la : info.la,
    ua: typeof input.ua === 'string' ? input.ua : info.ua,
    country: typeof input.country === 'string' ? input.country : info.country,
    ips: typeof input.ips === 'string' ? input.ips : info.ips || '',
    ...sanitizedCustom
  }

  if (userId != null && String(userId).trim()) payload.user_id = userId
  if (deviceId) payload.device_id = deviceId
  if (accountId != null && String(accountId).trim()) payload.account_id = String(accountId)
  if (info.bw) payload.bw = typeof input.bw === 'string' ? input.bw : info.bw

  if (!payload.ua) delete payload.ua
  if (!payload.country) delete payload.country

  return payload
}
